import React, { Suspense, lazy } from 'react';
import { css } from '@emotion/react';
import type { DynamicContentSlotEntryType, ConditionalContainerEntryType, RichTextEntryType } from 'tsconfig/types';
import { CmsRichText } from '../contentful/CmsRichText/CmsRichText';
import CmsDynamicContentSlot from '../contentful/CmsDynamicContentSlot/CmsDynamicContentSlot';
import { CmsConditionalContainer } from '../contentful/CmsConditionalContainer/CmsConditionalContainer';

// Lazy load the slider component to avoid bundling it when not needed
const LazyStripBannerSlider = lazy(() => import('./StripBannerSlider'));

const PAGE_SLUG_TO_GROUPING: Record<string, string> = {
  home: 'home',
  cart: 'cart',
  myaccount: 'myaccount',
  membership: 'myaccount',
  'Search PLP': 'plp',
  category: 'plp',
  pdp: 'pdp',
  'wine-preferences': 'myaccount',
};

export const hideStripBannerForPage = (
  pageSlug: string | undefined,
  stripBannerDisabledPages: string[] | undefined,
): boolean => {
  if (!pageSlug || !stripBannerDisabledPages) {
    return false; // Don't hide if pageSlug is undefined or no disabled pages specified
  }

  const pageGrouping = PAGE_SLUG_TO_GROUPING[pageSlug] || pageSlug;

  return stripBannerDisabledPages.includes(pageGrouping);
};

export const isRichTextType = (entry: unknown): entry is RichTextEntryType =>
  (entry as RichTextEntryType)?.sys?.contentType?.sys?.id === 'richtext';

export const isDynamicContentSlotType = (entry: unknown): entry is DynamicContentSlotEntryType =>
  (entry as DynamicContentSlotEntryType)?.sys?.contentType?.sys?.id === 'dynamicContentSlot';

const renderRichTextContent = (richTextItems: RichTextEntryType[], slickSliderWrapper?: string): JSX.Element | null => {
  if (richTextItems.length > 1) {
    const sliderWrapperCss = css`
      ${slickSliderWrapper}
    `;

    return (
      <Suspense fallback={<div className="strip-banner-slider-wrapper">Loading...</div>}>
        <LazyStripBannerSlider richTextItems={richTextItems} sliderWrapperCss={sliderWrapperCss} />
      </Suspense>
    );
  }

  if (richTextItems.length === 1) {
    return <CmsRichText contentItem={richTextItems[0]} />;
  }

  return null;
};

export const renderDynamicContentSlot = (
  stripBanner: DynamicContentSlotEntryType,
  slickSliderWrapper?: string,
): JSX.Element | null => {
  const layout = stripBanner.fields?.layout;

  if (!layout || layout.length === 0) {
    return null;
  }

  return (
    <CmsDynamicContentSlot contentItem={stripBanner}>
      {layout.filter(Boolean).map((item, index: number) => {
        const conditionalItem = item as ConditionalContainerEntryType;
        const richTextItems = (conditionalItem.fields?.content ?? []).filter(
          (entry): entry is RichTextEntryType => (entry as RichTextEntryType)?.sys?.contentType?.sys?.id === 'richtext',
        );

        return (
          <CmsConditionalContainer key={conditionalItem.sys?.id || index} contentItem={conditionalItem}>
            {renderRichTextContent(richTextItems, slickSliderWrapper)}
          </CmsConditionalContainer>
        );
      })}
    </CmsDynamicContentSlot>
  );
};
